import { Card, CardContent, Typography, Box, Grid, CircularProgress, Chip } from '@mui/material';
import { useEffect, useState } from 'react';
import { fetchSessionStats, fetchRealTimeMetrics } from '../../services/dashboard.service';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import { useFilterContext } from '../../usageandbilling/FilterContext';

interface SessionStatsData {
  totalSessions: number;
  totalSessionsChange: number | null;
  averageDuration: number;
  averageDurationChange: number | null;
  activeSessions: number;
}

interface RealTimeMetricsData {
  activeSessions: number;
  recentActiveUsers: number;
  todayFeatureUsage: {
    transcriptions: number;
    factFinds: number;
    meetings: number;
    calendlyMeetings?: number;
    clients: number;
    documents: number;
    aiAgentUsage: number;
    fileNotesGenerated: number;
    emailsSent: number;
  };
  unusualActivity: {
    transcriptions: boolean;
    factFinds: boolean;
    meetings: boolean;
    calendlyMeetings?: boolean;
    clients: boolean;
    documents: boolean;
    aiAgentUsage: boolean;
    fileNotesGenerated: boolean;
    emailsSent: boolean;
  };
}

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const LiveActivityCard = () => {
  const { selectedOrgIds: orgIds, selectedUserIds: userIds } = useFilterContext();
  const [sessionData, setSessionData] = useState<SessionStatsData | null>(null);
  const [realTimeData, setRealTimeData] = useState<RealTimeMetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (orgIds && orgIds.length > 0) params.set('orgIds', orgIds.join(','));
      if (userIds && userIds.length > 0) params.set('userIds', userIds.join(','));

      const [sessionRes, realTimeRes] = await Promise.all([
        fetchSessionStats(params.toString()),
        fetchRealTimeMetrics(params.toString())
      ]);

      setSessionData(sessionRes as SessionStatsData);
      setRealTimeData(realTimeRes as RealTimeMetricsData);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch live activity data:', err);
      setError('Failed to load live activity data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchData();

    // Auto-refresh every 30 seconds
    const intervalId = setInterval(fetchData, 30000);
    return () => clearInterval(intervalId);
  }, [orgIds, userIds]);

  if (loading) {
    return (
      <Card sx={{ p: 2, height: '100%' }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error || !sessionData || !realTimeData) {
    return (
      <Card sx={{ p: 2, height: '100%' }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={2}>Live Activity</Typography>
          <Typography color="error">{error || 'No data available'}</Typography>
        </CardContent>
      </Card>
    );
  }

  const renderChange = (change: number | null) => {
    if (change === null || !isFinite(change)) return null;
    const isPositive = change >= 0;
    return (
      <Box sx={{
        bgcolor: isPositive ? '#E6F4EA' : '#FDEDED',
        color: isPositive ? '#2E7D32' : '#C62828',
        borderRadius: '6px', px: 0.5, py: 0.25, ml: 0.5
      }}>
        <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.65rem' }}>
          {isPositive ? '+' : ''}{change.toFixed(0)}%
        </Typography>
      </Box>
    );
  };

  const { todayFeatureUsage, unusualActivity } = realTimeData;

  const todayMetrics = [
    { label: 'Transcriptions', value: todayFeatureUsage.transcriptions, spike: unusualActivity.transcriptions },
    { label: 'Fact Finds', value: todayFeatureUsage.factFinds, spike: unusualActivity.factFinds },
    { label: 'Meetings', value: todayFeatureUsage.meetings, spike: unusualActivity.meetings },
    { label: 'Calendly', value: todayFeatureUsage.calendlyMeetings ?? 0, spike: unusualActivity.calendlyMeetings ?? false },
    { label: 'Clients', value: todayFeatureUsage.clients, spike: unusualActivity.clients },
    { label: 'Documents', value: todayFeatureUsage.documents, spike: unusualActivity.documents },
    { label: 'AI Agent', value: todayFeatureUsage.aiAgentUsage, spike: unusualActivity.aiAgentUsage },
    { label: 'File Notes', value: todayFeatureUsage.fileNotesGenerated, spike: unusualActivity.fileNotesGenerated },
    { label: 'Emails Sent', value: todayFeatureUsage.emailsSent, spike: unusualActivity.emailsSent },
  ];

  return (
    <Card sx={{ p: 1.5, height: '100%' }}>
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="subtitle1" fontWeight="bold">Live Activity</Typography>
          <Chip label="Auto-refresh" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
        </Box>

        {/* Active Sessions & Users Row */}
        <Box sx={{ bgcolor: '#F5F5F5', borderRadius: 2, p: 1.5, mb: 1.5 }}>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <PeopleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography variant="caption" color="text.secondary">Active Sessions</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="h6" fontWeight="bold" color="primary">{realTimeData.activeSessions}</Typography>
                <Box sx={{
                  width: 8, height: 8, borderRadius: '50%',
                  bgcolor: realTimeData.activeSessions > 0 ? '#4CAF50' : '#9E9E9E',
                  animation: realTimeData.activeSessions > 0 ? 'pulse 2s infinite' : 'none',
                  '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.5 }, '100%': { opacity: 1 } }
                }} />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">Avg Duration</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" fontWeight="bold">{formatDuration(sessionData.averageDuration)}</Typography>
                {renderChange(sessionData.averageDurationChange)}
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Active (15m)</Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">{realTimeData.recentActiveUsers}</Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Today's Activity Grid */}
        <Typography variant="caption" fontWeight="bold" color="text.secondary" mb={1} display="block">Today's Activity</Typography>
        <Grid container spacing={1}>
          {todayMetrics.map((m, i) => (
            <Grid item xs={3} key={i}>
              <Box sx={{ textAlign: 'center', p: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block' }}>{m.label}</Typography>
                <Box display="flex" justifyContent="center" alignItems="center" gap={0.25}>
                  <Typography variant="body2" fontWeight="medium">{m.value}</Typography>
                  {m.spike && <TrendingUpIcon sx={{ fontSize: 10, color: '#E65100' }} />}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default LiveActivityCard;
